import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal } from 'antd';

import { canonicalize } from '../../utils/kqlBuilder';
import { PICKABLE_REFUSALS, answerRefusal, refusalChoices } from '../../utils/filterValues';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from './QueryRefusalPick';
import FilterChips from '../common/FilterChips';
import ShowAsText from '../common/ShowAsText';

export const DUPLICATE_NAME_MESSAGE = 'You already have a filter with this name';

/** The window's title and button, per what it is for. */
const MODES = {
	save: { title: 'Save filter', ok: 'Save' },
	'save-new': { title: 'Save as a new filter', ok: 'Save' },
	rename: { title: 'Rename saved filter', ok: 'Save' },
	update: { title: null, ok: 'Update' },
};

/**
 * THE SAVED FILTER'S NAME WINDOW (KPR-1642; the save half of what was the
 * chips' picker). One window for four jobs: **Save filter** (nothing applied),
 * **Save as new** (from a changed filter), **Rename**, and **Update** when the
 * server asks the person to pick what a name means first.
 *
 * A save the server refuses for something the person can answer by picking (a
 * record named by its title, a name two cards, sets or choices share: the
 * deterministic filters F2/F3; an element name several elements answer to:
 * element ids step 3) stays open with the picks as buttons, and the save goes
 * again with the pick. A duplicate name (409) is said in the window, where the
 * person is still typing.
 *
 * `naming` is `{ mode, filter?, error? }` (null closes it): `error` is the
 * refusal an Update met before the window opened. `draft` is what the screen
 * would save (utils/savedFilterState `savedFilterDraft`).
 */
export default function SavedFilterNameModal({
	naming,
	draft,
	fields = null,
	boardText = '',
	onClose,
	save,
	rename,
	update,
	onSaved,
}) {
	const mode = naming?.mode || 'save';
	const words = MODES[mode] || MODES.save;
	const [name, setName] = useState('');
	const [nameError, setNameError] = useState(null);
	const [saving, setSaving] = useState(false);
	// A refusal answered by picking: `{ code, value, element, candidates, query }`.
	const [refusal, setRefusal] = useState(null);
	// The text the picks have made of the filter: what every later save sends
	// while the window stays open (the F3 review's F9).
	const [pickedQuery, setPickedQuery] = useState(null);
	const draftQuery = draft?.query || '';
	const canonicalQuery = useMemo(() => canonicalize(draftQuery), [draftQuery]);
	// …answering exactly the text it was made for: a new one forgets them.
	const elementPicks = useElementChoices(canonicalQuery || draftQuery);
	const { reset: resetElementPicks, take: takeElementRefusal } = elementPicks;

	// Says what a refusal asks, in the window. True when it was one to answer here.
	const takeRefusal = useCallback((error, sent) => {
		const data = error?.response?.data || {};
		if (takeElementRefusal(error)) {
			setRefusal(null);
			setNameError(null);
			return true;
		}
		if (error?.response?.status === 400 && PICKABLE_REFUSALS.has(data.code) && refusalChoices(data, { fields }).length) {
			setRefusal({ ...data, query: sent });
			setNameError(data.message || null);
			return true;
		}
		return false;
	}, [takeElementRefusal, fields]);

	// Opening: the name it starts from, and the refusal an Update already met.
	const open = Boolean(naming);
	useEffect(() => {
		if (!open) return;
		setName(naming?.mode === 'rename' || naming?.mode === 'update' ? (naming?.filter?.name || '') : '');
		setNameError(null);
		setRefusal(null);
		setPickedQuery(null);
		resetElementPicks();
		if (naming?.error) takeRefusal(naming.error, draftQuery);
		// Only on opening: the draft moving under an open window is not a reopen.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, naming]);

	const submit = useCallback(async (override = null) => {
		const trimmed = name.trim();
		if (!trimmed && mode !== 'update') {
			setNameError('Give the filter a name.');
			return;
		}
		setSaving(true);
		const sent = override || pickedQuery || canonicalQuery || draftQuery;
		try {
			let saved;
			if (mode === 'rename') saved = await rename(naming.filter, trimmed);
			else if (mode === 'update') saved = await update(naming.filter, elementPicks.withChoices({ ...draft, query: sent }));
			else saved = await save(elementPicks.withChoices({ ...draft, name: trimmed, query: sent }));
			onClose();
			if (saved && typeof onSaved === 'function') onSaved(saved, mode);
		} catch (error) {
			if (takeRefusal(error, sent)) return;
			const data = error?.response?.data || {};
			setNameError(error?.response?.status === 409
				? DUPLICATE_NAME_MESSAGE
				: (data.message || (mode === 'update' ? 'Could not update the saved filter.' : 'Could not save the filter.')));
		} finally {
			setSaving(false);
		}
	}, [name, mode, naming, pickedQuery, canonicalQuery, draftQuery, draft, rename, update, save, elementPicks, onClose, onSaved, takeRefusal]);

	const pickForRefusal = useCallback((value) => {
		if (!refusal) return;
		const next = answerRefusal(refusal.query, refusal, value, { fields });
		setRefusal(null);
		setNameError(null);
		setPickedQuery(next);
		submit(next);
	}, [refusal, submit, fields]);

	const shownQuery = pickedQuery || canonicalQuery;
	const title = mode === 'update' ? `Update "${naming?.filter?.name || ''}"` : words.title;

	return (
		<Modal
			open={open}
			title={title}
			okText={words.ok}
			onOk={() => submit()}
			confirmLoading={saving}
			onCancel={onClose}
			destroyOnHidden
			className="saved-filters__modal"
		>
			{mode === 'update' ? null : (
				<>
					<label className="saved-filters__name-label" htmlFor="saved-filter-name">Name</label>
					<Input
						id="saved-filter-name"
						autoFocus
						maxLength={100}
						value={name}
						placeholder="e.g. Molly's recent notes"
						onChange={event => { setName(event.target.value); setNameError(null); }}
						onPressEnter={() => submit()}
					/>
				</>
			)}
			{nameError ? (
				<div className="saved-filters__name-error" role="alert">{nameError}</div>
			) : null}
			<QueryRefusalPick
				inline
				className="saved-filters__element-pick"
				refusal={elementPicks.refusal}
				disabled={saving}
				onPick={(value) => { elementPicks.pick(value); submit(); }}
			/>
			{refusal ? (
				<div className="saved-filters__picks" role="group" aria-label={refusal.code === 'lookup_needs_item' ? 'Pick the record it means' : 'Pick the one it means'}>
					{refusalChoices(refusal, { fields }).map(choice => (
						<Button key={choice.key || choice.value} onClick={() => pickForRefusal(choice.value)} disabled={saving}>
							{choice.label}
						</Button>
					))}
				</div>
			) : null}
			{mode !== 'rename' && shownQuery ? (
				<div className="saved-filters__name-hint">
					<p className="saved-filters__name-hint-lead">{mode === 'update' ? 'Writes the filter now on screen:' : 'Saves the filter now on screen:'}</p>
					<FilterChips query={shownQuery} fields={fields || []} readOnly />
					<ShowAsText text={shownQuery} label="Filter as text" fields={fields || []} />
					{boardText ? <p className="saved-filters__name-hint-board">It opens as {boardText}.</p> : null}
				</div>
			) : null}
		</Modal>
	);
}
